<script lang="ts" setup>
import type { TreeNode } from '#layers/core/app/components/ideo/types';

defineOptions({
    name: 'WidgetCustomMenuSettings'
});

interface PageModel
{
    id: number;
    parentId?: number;
    name: string;
    hasChildren: boolean;
    position: number;
    logo?: FileResource;
    isCategory: boolean;
    isUrl: boolean;
    urlPath?: string;
    urlName?: string;
    urlTargetBlank: boolean;
    categoryName?: string;
    pageId?: number;
    columns?: number;
    selectedType: Types;
}

export interface FormSettings
{
    settings: {
        model: PageModel[];
    };
}

const { $t } = useLocalization();

enum Types
{
    Category = 'Category',
    Page = 'Page',
    Url = 'Url'
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    model: [
        {
            id: Date.now(),
            parentId: null,
            name: $t('[[[Nowa kategoria]]]'),
            position: 1,
            logo: null,
            isCategory: true,
            isUrl: false,
            urlTargetBlank: false,
            categoryName: $t('[[[Nowa kategoria]]]'),
            pageId: null,
            hasChildren: false,
            columns: 1,
            selectedType: Types.Category
        }
    ]
};

const columns = [
    { value: 1, text: '1' },
    { value: 2, text: '2' },
    { value: 3, text: '3' },
    { value: 4, text: '4' }
];

const expanded = ref([]);
const selected = ref<number | null>(null);
const editoPageSelectorRef = ref<InstanceType<any>>(null);

const page = computed(() => editoPageSelectorRef.value?.page);
const nodes = computed(() => model.value.settings?.model ?? []);
const form = computed(() => nodes.value.find(p => p.id == selected.value));
const parentIdOptions = computed(() =>
{
    const map: any = {};
    const result: any[] = [];

    nodes.value.forEach((item) =>
    {
        const disabled = item.id === form.value?.id;

        map[item.id] = {
            key: item.id,
            label: item.name.trim(),
            children: [],
            selectable: !disabled,
            styleClass: { 'p-tree-node-disabled': disabled }
        };
    });

    nodes.value.forEach((item) =>
    {
        if (item.parentId === null)
        {
            result.push(map[item.id]);
        }
        else
        {
            if (map[item.parentId])
            {
                map[item.parentId].children.push(map[item.id]);
            }
        }
    });

    return result;
});

function onAdd(): void
{
    nodes.value.push({ ...defaults.model[0], id: Date.now(), position: nodes.value.length + 1 });
}

function onDelete(node: PageModel): void
{
    const index = nodes.value.findIndex(p => p.id == node.id);

    if (node.id == selected.value)
    {
        selected.value = null;
    }

    nodes.value.splice(index, 1);

    if (node.hasChildren)
    {
        const children = nodes.value.filter(p => p.parentId == node.id);

        children.forEach(child => onDelete(child));
    }
}

const getNodes = () =>
{
    return nodes.value;
};

const getMenu = async (): Promise<any[]> =>
{
    const menu: any[] = [
        {
            name: $t('[[[Usuń]]]'),
            icon: 'icon far fa-trash-alt',
            class: 'text-danger',
            click: onDelete,
            confirm: $t('[[[Potwierdzenie usunięcia]]]'),
            confirmPlacement: 'bottom'
        }
    ];

    return menu;
};

const canSort = (): boolean =>
{
    return true;
};

const onToggle = async (node: TreeNode): Promise<void> =>
{
    if (expanded.value.indexOf(node.id) >= 0)
    {
        expanded.value = expanded.value.filter(p => p !== node.id);
    }
    else
    {
        expanded.value.push(node.id);
    }
};

const onClick = (node: TreeNode): void =>
{
    selected.value = node?.id as number;
};

const onSort = async (items: PageModel[]): Promise<void> =>
{
    items.forEach((pos) =>
    {
        const node = nodes.value.find(node => node.id == pos.id);

        if (node) node.position = pos.position;
    });
};

const active = (node: TreeNode): boolean =>
{
    return node.id == selected.value;
};

const updateParentId = (value: number): void =>
{
    const oldNode = nodes.value.find(p => p.id == form.value.parentId);

    form.value.parentId = value || null;

    if (oldNode)
    {
        oldNode.hasChildren = nodes.value.some(p => p.parentId == oldNode.id);
    }

    const newNode = nodes.value.find(p => p.id == value);

    if (newNode)
    {
        newNode.hasChildren = true;
    }
};

const updateIsCategory = (value: Types): void =>
{
    if (value === Types.Category)
    {
        form.value.name = form.value.categoryName ?? $t('[[[-]]]');
    }

    if (value === Types.Page)
    {
        form.value.name = page.value?.name ?? $t('[[[-]]]');
    }

    if (value === Types.Url)
    {
        form.value.name = form.value.urlName ?? $t('[[[-]]]');
    }

    form.value.isCategory = value === Types.Category;

    form.value.isUrl = value === Types.Url;
};

watch(() => form.value?.categoryName, (value) =>
{
    if (form.value && form.value.isCategory && !form.value.isUrl)
    {
        form.value.name = value || $t('[[[-]]]');
    }
});

watch(() => page.value, (value) =>
{
    if (form.value && !form.value.isCategory && !form.value.isUrl)
    {
        form.value.name = value?.name?.trim?.() || $t('[[[-]]]');
    }
},
{ flush: 'post' });

watch(() => form.value?.urlName, (value) =>
{
    if (form.value && form.value.isUrl)
    {
        form.value.name = value || $t('[[[-]]]');
    }
},
{ flush: 'post' });

onMounted(async () =>
{
    model.value.settings = { ...defaults, ...model.value.settings };

    const min = nodes.value
        .filter(item => item.parentId == null)
        .reduce((prev, current) => (prev?.position < current.position ? prev : current), null);

    selected.value = min?.id;
});
</script>

<template>
    <div>
        <IdeoButton icon="fas fa-plus" class="mb-2" @click="onAdd">
            {{ $t('[[[Dodaj]]]') }}
        </IdeoButton>
        <div v-if="getNodes().length == 0">
            <p class="text-center py-3 mb-0">
                {{ $t('[[[Nie ma jeszcze żadnych rekordów]]]') }}
            </p>
        </div>
        <div v-else class="row">
            <div class="col-md-5">
                <IdeoTree
                    :pages="getNodes()"
                    :expanded="expanded"
                    :menu="getMenu"
                    :active="active"
                    :move="canSort"
                    @toggle="onToggle"
                    @sort="onSort"
                    @click="onClick"
                />
            </div>
            <div v-if="selected" class="col-md-7">
                <IdeoFormGroup :label="$t('[[[Strona/kategoria nadrzędna]]]')">
                    <IdeoFormTreeSelect
                        name="parentId"
                        :empty-option="$t('[[[Najwyższy poziom]]]')"
                        :model-value="form.parentId"
                        :options="parentIdOptions"
                        :placeholder="$t('[[[Najwyższy poziom]]]')"
                        @update:model-value="updateParentId"
                    />
                </IdeoFormGroup>
                <IdeoFormGroup :label="$t('[[[Zdjęcie/ikonka]]]')">
                    <IdeoFilePicker v-model="form.logo" media="images" />
                </IdeoFormGroup>
                <IdeoFormRadio v-model="form.selectedType" :value="Types.Category" name="selectedType" @update:model-value="updateIsCategory">
                    {{ $t('[[[Kategoria]]]') }}
                </IdeoFormRadio>
                <IdeoFormRadio v-model="form.selectedType" :value="Types.Page" name="selectedType" @update:model-value="updateIsCategory">
                    {{ $t('[[[Strona]]]') }}
                </IdeoFormRadio>
                <IdeoFormRadio v-model="form.selectedType" :value="Types.Url" name="selectedType" @update:model-value="updateIsCategory">
                    {{ $t('[[[Adres URL]]]') }}
                </IdeoFormRadio>
                <IdeoFormGroup v-if="form.isCategory && !form.isUrl" :label="$t('[[[Nazwa kategorii]]]')" required>
                    <IdeoFormInput v-model="form.categoryName" type="text" />
                </IdeoFormGroup>
                <IdeoFormGroup v-if="!form.isCategory && !form.isUrl" :label="$t('[[[Wybierz stronę]]]')">
                    <EditoPageSelector
                        ref="editoPageSelectorRef"
                        v-model="form.pageId"
                        :module="null"
                        label=""
                        :preview="false"
                        :no-preview="true"
                        class="mb-0"
                    />
                </IdeoFormGroup>
                <IdeoFormGroup v-if="form.isUrl" :label="$t('[[[Adres]]]')">
                    <IdeoFormInput v-model="form.urlPath" type="text" />
                </IdeoFormGroup>
                <IdeoFormGroup v-if="form.isUrl" :label="$t('[[[Nazwa adresu]]]')">
                    <IdeoFormInput v-model="form.urlName" type="text" />
                </IdeoFormGroup>
                <IdeoFormGroup v-if="form.isUrl">
                    <IdeoFormCheckbox v-model="form.urlTargetBlank">
                        {{ $t('[[[Otwórz w nowej karcie]]]') }}
                    </IdeoFormCheckbox>
                </IdeoFormGroup>
                <IdeoFormGroup :label="$t('[[[Ilość kolumn podkategorii]]]')">
                    <IdeoFormSelect v-model="form.columns" :options="columns" />
                </IdeoFormGroup>
            </div>
        </div>
    </div>
</template>
